import React, { useState } from 'react'
import Button from 'components/shared/Button'
import { FormControlLabel, Checkbox, Tooltip, Chip } from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import styles from 'components/forms/Checkbox/Checkbox.module.scss'
import strings from 'strings'

const DEMO_ORG_SUGGESTIONS = ['pharmacy', 'medical', 'health']
const DEMO_INDIVIDUAL_SUGGESTIONS = ['smith', 'jones', 'garcia']

const fieldWrap = { display: 'flex', flexDirection: 'column', flex: '1 1 200px', minWidth: 160 }
const fieldRow = { display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }
const inputStyle = { padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 }
const labelStyle = { fontSize: 12, marginBottom: 4, color: '#555', display: 'flex', alignItems: 'center', gap: 4 }

const TOOLTIPS = {
    npi: 'National Provider Identifier — 10-digit unique ID. If provided, an exact NPI match short-circuits the fuzzy search.',
    dob: 'Date of birth (YYYY-MM-DD). Sent to the backend as YYYYMMDD to match LEIE\'s native date format.',
    state: 'Two-letter state code (e.g. CA). Filters by the excluded party\'s state of record.',
    excludeReinstated: 'HHS OIG can lift an exclusion — reinstated rows keep is_active=false with a rein_date populated. Uncheck to include them in results.',
}

// Convert a YYYY-MM-DD user input to the YYYYMMDD form the backend expects. Backend
// stores dob as an 8-char digit string; sending a dashed value would miss the index.
const normalizeDobForPayload = (dob) => {
    if (!dob) return null
    const digits = dob.replace(/[^0-9]/g, '')
    return digits.length === 8 ? digits : dob
}

const LeieSearchFields = ({ loading, handleSearch, isQuickSearch, isDemoMode, prefillTargets }) => {
    // In an application tab, prefill busName from the business by default; a chip row lets the
    // user swap to search for an individual owner instead. LEIE's backend AND's non-null predicates
    // across the two "sides" of the record (person fields vs business field), so mixing a business
    // name with a person name returns zero.
    const businessPrefill = prefillTargets?.business || ''
    const [npi, setNpi] = useState('')
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [busName, setBusName] = useState(() => businessPrefill)
    const [dob, setDob] = useState('')
    const [state, setState] = useState('')
    const [selectedPrefill, setSelectedPrefill] = useState(() => businessPrefill ? 'business' : null)
    const [excludeReinstated, setExcludeReinstated] = useState(false)

    const canSubmit = npi || firstName || lastName || busName || dob

    const onSubmit = () => {
        if (loading) return
        handleSearch({
            npi: npi || null,
            firstName: firstName || null,
            lastName: lastName || null,
            busName: busName || null,
            dob: normalizeDobForPayload(dob),
            state: state || null,
            excludeReinstated,
        })
    }

    const labelWithTooltip = (text, tooltipText) => (
        <label style={labelStyle}>
            {text}
            {tooltipText && (
                <Tooltip title={tooltipText} arrow placement='top'>
                    <InfoOutlinedIcon style={{ fontSize: 14, color: '#888', cursor: 'help' }} />
                </Tooltip>
            )}
        </label>
    )

    const field = (labelEl, value, setter, id, placeholder = '', type = 'text') => (
        <div style={fieldWrap}>
            {labelEl}
            <input
                style={inputStyle}
                value={value}
                onChange={(e) => setter(e.target.value)}
                placeholder={placeholder}
                id={id}
                type={type}
            />
        </div>
    )

    const applyOrgSuggestion = (term) => {
        setBusName(term)
        setFirstName('')
        setLastName('')
        setNpi('')
    }
    const applyIndividualSuggestion = (term) => {
        setLastName(term)
        setBusName('')
        setNpi('')
    }

    // Prefill picker — swap the search target between the application's business and any of its
    // owners. Clears the opposite fields each time.
    const selectBusiness = () => {
        setBusName(businessPrefill)
        setFirstName('')
        setLastName('')
        setSelectedPrefill('business')
    }
    const selectOwner = (owner, index) => {
        setBusName('')
        setFirstName(owner.firstName || '')
        setLastName(owner.lastName || '')
        setSelectedPrefill(`owner-${index}`)
    }
    const owners = prefillTargets?.owners || []
    const showPrefillPicker = businessPrefill || owners.length > 0
    const prefillChipStyle = (isSelected) => ({
        background: isSelected ? 'var(--rox-lilac-ink)' : 'var(--badge-review-bg)',
        color: isSelected ? '#fff' : 'var(--rox-lilac-ink)',
        fontWeight: isSelected ? 600 : 400,
    })

    return (
        <div>
            {showPrefillPicker && (
                <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--bg-canvas)', borderRadius: 4, fontSize: 12 }}>
                    <div style={{ color: '#555', marginBottom: 6 }}>
                        <strong>Prefill from application</strong> — click to switch search target:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {businessPrefill && (
                            <Chip
                                label={`Business: ${businessPrefill}`}
                                size='small'
                                clickable
                                onClick={selectBusiness}
                                style={prefillChipStyle(selectedPrefill === 'business')}
                            />
                        )}
                        {owners.map((owner, i) => (
                            <Chip
                                key={i}
                                label={`Owner: ${owner.label}`}
                                size='small'
                                clickable
                                onClick={() => selectOwner(owner, i)}
                                style={prefillChipStyle(selectedPrefill === `owner-${i}`)}
                            />
                        ))}
                    </div>
                </div>
            )}
            {isDemoMode && (
                <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f4ff', borderRadius: 4, fontSize: 12 }}>
                    <div style={{ color: '#555', marginBottom: 6 }}>
                        <strong>Demo mode suggestions</strong> — click to fill:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ color: '#666', minWidth: 90 }}>Businesses →</span>
                        {DEMO_ORG_SUGGESTIONS.map(term => (
                            <Chip
                                key={term}
                                label={term}
                                size='small'
                                clickable
                                onClick={() => applyOrgSuggestion(term)}
                                style={{ background: '#fff' }}
                            />
                        ))}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                        <span style={{ color: '#666', minWidth: 90 }}>Individuals →</span>
                        {DEMO_INDIVIDUAL_SUGGESTIONS.map(term => (
                            <Chip
                                key={term}
                                label={term}
                                size='small'
                                clickable
                                onClick={() => applyIndividualSuggestion(term)}
                                style={{ background: '#fff' }}
                            />
                        ))}
                    </div>
                </div>
            )}
            <div style={fieldRow}>
                {field(labelWithTooltip('NPI', TOOLTIPS.npi), npi, setNpi, 'leieNpi', '10-digit NPI (exact match)')}
                {field(labelWithTooltip('DOB', TOOLTIPS.dob), dob, setDob, 'leieDob', 'YYYY-MM-DD')}
                {field(labelWithTooltip('State', TOOLTIPS.state), state, setState, 'leieState', 'e.g. CA')}
            </div>
            <div style={fieldRow}>
                {field(labelWithTooltip('First Name'), firstName, setFirstName, 'leieFirstName')}
                {field(labelWithTooltip('Last Name'), lastName, setLastName, 'leieLastName')}
                {field(labelWithTooltip('Business Name'), busName, setBusName, 'leieBusName', 'e.g. Kaiser')}
            </div>
            <div className='d-flex align-items-center justify-content-between mt-2' style={{ gap: '16px' }}>
                <FormControlLabel
                    className={styles.centeredCheckbox}
                    control={
                        <Checkbox
                            id='leieExcludeReinstated'
                            checked={excludeReinstated}
                            onChange={(e) => setExcludeReinstated(e.target.checked)}
                            color='primary'
                        />
                    }
                    label={
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            Hide reinstated exclusions
                            <Tooltip title={TOOLTIPS.excludeReinstated} arrow placement='top'>
                                <InfoOutlinedIcon style={{ fontSize: 14, color: '#888', cursor: 'help' }} />
                            </Tooltip>
                        </span>
                    }
                />
                <Button
                    onClick={onSubmit}
                    loading={loading}
                    disabled={!canSubmit}
                    id={`perform-leie-search-${isQuickSearch ? 'qs' : 'app'}`}
                    style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                    {strings.SEARCH}
                </Button>
            </div>
        </div>
    )
}

export default LeieSearchFields
